/**
 * The shared footer every card's `actions`/`links` draw through.
 *
 * One component, wired into every card the backend attaches `actions`/`links`
 * to (design doc §11.4-§11.5), so the tap bridge (`dispatchTap`) is used in
 * exactly one place rather than re-implemented per card. `ObjectCard` used to
 * be the exception — `send(action.value ?? action.label)`, mid-`§11.1` review
 * — and is the reason this file exists rather than staying inline there.
 *
 * `links` open a new tab and never touch the bridge — `outbound_url` policy
 * already refuses a link whose host is not allow-listed on the backend, so a
 * link that reaches the wire is one the graph already vetted.
 */

import { useSurface } from '../../a2ui/surfaceStore';
import { dispatchTap } from '../../a2ui/actions';
import { cardActionTap, type CardAction, type CardLink } from '../cardActions';
import { useCardLive } from '../inlineCard';
import { stripEmoji as plain } from '../../lib/emoji';
import { useSendLocked } from '../../chat/turnLock';

export default function CardActions({
  sourceId,
  actions,
  links,
}: {
  /** The card's own `id` — every catalogue node carries one (`common.id`). */
  sourceId: string;
  actions?: readonly CardAction[] | null;
  links?: readonly CardLink[] | null;
}) {
  const surfaceId = useSurface((state) => state.surfaceId);
  // Under an answer (`chat/TurnCards.tsx`) an action names a card that may have
  // left the board since — drawn only while it has not, or, on a board-less
  // graph, only on the newest snapshot (`inlineCard.ts`). On the board this is
  // always true and nothing changes.
  const live = useCardLive(sourceId);
  const sendLocked = useSendLocked();
  const actionList = live ? (actions ?? []) : [];
  const linkList = links ?? [];
  if (actionList.length === 0 && linkList.length === 0) return null;

  return (
    <footer className="card__actions">
      {actionList.map((action, index) => (
        <button
          key={`${action.label}:${index}`}
          type="button"
          className="chip"
          disabled={sendLocked}
          // Byte for byte, through the bridge: `a2ui_action` now travels for a
          // tap on a card exactly as it does for a chip (`Chips.tsx`).
          onClick={() => dispatchTap(cardActionTap(action), sourceId, surfaceId)}
        >
          {plain(action.label)}
        </button>
      ))}
      {linkList.map((link) => (
        <a
          key={link.url}
          className="chip chip--link"
          href={link.url}
          target="_blank"
          rel="noreferrer noopener"
        >
          {plain(link.label)}
        </a>
      ))}
    </footer>
  );
}
